import React from 'react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const PerformanceTrendChart = ({ data, title, type = 'line' }) => {
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-12 shadow-elevation-lg">
          <p className="text-sm font-medium text-popover-foreground mb-8">{label}</p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center gap-8 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              />
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-popover-foreground data-text">
                {entry?.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-16 md:p-20 lg:p-24 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-16 md:mb-20">
        <h3 className="text-lg md:text-xl font-semibold text-foreground font-heading">
          {title}
        </h3>
        <div className="flex items-center gap-8">
          <button className="p-8 rounded-md hover:bg-muted transition-smooth">
            <Icon name="Download" size={18} className="text-muted-foreground" />
          </button>
          <button className="p-8 rounded-md hover:bg-muted transition-smooth">
            <Icon name="Maximize2" size={18} className="text-muted-foreground" />
          </button>
        </div>
      </div>

      <div className="w-full h-64 md:h-80 lg:h-96">
        <ResponsiveContainer width="100%" height="100%">
          {type === 'area' ? (
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--color-primary))" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="hsl(var(--color-primary))" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorTarget" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--color-success))" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="hsl(var(--color-success))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--color-border))" />
              <XAxis 
                dataKey="month" 
                stroke="hsl(var(--color-muted-foreground))"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="hsl(var(--color-muted-foreground))"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="hsl(var(--color-primary))" 
                fillOpacity={1}
                fill="url(#colorRevenue)"
                name="Revenue"
              />
              <Area 
                type="monotone" 
                dataKey="target" 
                stroke="hsl(var(--color-success))" 
                fillOpacity={1}
                fill="url(#colorTarget)"
                name="Target"
              />
            </AreaChart>
          ) : (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--color-border))" />
              <XAxis 
                dataKey="month" 
                stroke="hsl(var(--color-muted-foreground))"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="hsl(var(--color-muted-foreground))"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
              />
              <Line 
                type="monotone" 
                dataKey="winRate" 
                stroke="hsl(var(--color-primary))" 
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Win Rate"
              />
              <Line 
                type="monotone" 
                dataKey="slaCompliance" 
                stroke="hsl(var(--color-success))" 
                strokeWidth={2}
                dot={{ r: 4 }}
                name="SLA Compliance"
              />
              <Line 
                type="monotone" 
                dataKey="deliveryQuality" 
                stroke="hsl(var(--color-warning))" 
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Delivery Quality"
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerformanceTrendChart;